import type { ReactNode } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Drawer,
  useMediaQuery,
  useTheme,
  IconButton,
  Stack,
  Typography,
  alpha,
  styled,
} from "@mui/material";

import { CARD_BORDER_RADIUS } from "@/constants/layout";
import { Z_INDEX } from "@/constants/zIndex";
import { panelWidth } from "@/theme/mixins";

// The original's shared drawer shell (`a$`/`aF` in the `_app` bundle) is a
// SwipeableDrawer anchored `right` on desktop and `bottom` below the `lg`
// breakpoint — which this app redefines as 900px, NOT MUI's 1200. Its paper is
// `height: 100%`, and on the bottom-sheet side it takes 20px top corners and
// `max-height: 95%`. The width itself comes from the shared `panelWidth`
// mixin — 100vw under `lg`, 360px to `xxl`, then 30vw capped at 500 — which is
// why every side panel measures identically at a given window width.
const Panel = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  marginLeft: "auto",
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.paper,
  borderRadius: 0,
  overflow: "hidden",
  [theme.breakpoints.down("lg")]: {
    height: "100%",
    borderRadius: `${CARD_BORDER_RADIUS}px ${CARD_BORDER_RADIUS}px 0 0`,
  },
}));

// 50x8 divider-coloured pill pinned 8px from the top of the sheet, with the
// original's shadow and 1px paper border. Mobile only.
const Grabber = styled(Box)(({ theme }) => ({
  width: 50,
  height: 8,
  backgroundColor: theme.palette.divider,
  borderRadius: 999,
  position: "absolute",
  top: 8,
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: Z_INDEX.LOW,
  boxShadow: theme.shadows[3],
  border: `1px solid ${theme.palette.background.paper}`,
}));

const Header = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  // The grabber floats over the top of the sheet, so the header clears it.
  padding: isMobile ? theme.spacing(2.5, 2, 1.5) : theme.spacing(1, 2, 1.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
}));

const Body = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  overflowY: "auto",
  flex: 1,
}));

const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2, 2),
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  backgroundColor: theme.palette.background.paper,
}));

/** Rounded bottom sheet used by every drawer surface in the app. */
interface BottomDrawerProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children?: ReactNode;
  footer?: ReactNode;
  /**
   * Suppress the shared header. Several of the original's drawers draw their
   * own — a back arrow and an `h5`, with no close button — because they are
   * screens inside the About drawer's stack as well as top-level drawers.
   */
  hideHeader?: boolean;
  /** Let a drawer own its body padding, as the policy screens do. */
  disableBodyPadding?: boolean;
  /** Optional per-surface motion budget. Item details use a shorter transition
   * so animation cannot dominate INP on slower devices. */
  transitionDuration?: number;
}

export default function BottomDrawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  hideHeader,
  disableBodyPadding,
  transitionDuration,
}: BottomDrawerProps) {
  const theme = useTheme();
  // `lg` is 900px here (constants/layout.ts), which is where the original
  // switches its drawers from the right edge to a bottom sheet.
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  return (
    <Drawer
      anchor={isMobile ? "bottom" : "right"}
      open={open}
      onClose={onClose}
      closeAfterTransition
      disableRestoreFocus
      transitionDuration={transitionDuration}
      sx={(theme) => ({
        zIndex: Z_INDEX.DRAWER,
        height: "100%",
        ...panelWidth(theme),
      })}
      PaperProps={{
        sx: {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100%",
          ...(isMobile ? { maxHeight: "95%" } : null),
        },
      }}
    >
      <Panel sx={{ position: "relative" }}>
        {isMobile && <Grabber />}
        {!hideHeader && (
        <Header isMobile={isMobile}>
          <Box minWidth={0}>
            <Typography
              component="h2"
              variant="subtitle1"
              fontWeight={700}
              noWrap
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            sx={{ width: 44, height: 44 }}
          >
            <CloseIcon />
          </IconButton>
        </Header>
        )}
        <Body sx={disableBodyPadding ? { p: 0 } : undefined}>{children}</Body>
        {footer && <Footer>{footer}</Footer>}
      </Panel>
    </Drawer>
  );
}
